import { useMemo } from 'react';
import { isNil } from 'lodash';
import { EQUITY_VALUE } from 'common/formulas/math.js';
import {
  UseWaterfallComputedValuesParams,
  WaterfallComputedValues,
} from './useWaterfallComputedValues.props';

export type { UseWaterfallComputedValuesParams, WaterfallComputedValues } from './useWaterfallComputedValues.props';

export const useWaterfallComputedValues = ({
  equityValue,
  enterpriseValue,
  cashOverride,
  debtOverride,
  financials,
}: UseWaterfallComputedValuesParams): WaterfallComputedValues => {
  // Determine effective cash/debt values
  const effectiveCash = useMemo(() => {
    // If cashOverride is explicitly set (including 0), use it
    if (!isNil(cashOverride)) {
      return cashOverride;
    }
    return financials.plusCash;
  }, [cashOverride, financials.plusCash]);

  const effectiveDebt = useMemo(() => {
    // If debtOverride is explicitly set (including 0), use it
    if (!isNil(debtOverride)) {
      return debtOverride;
    }
    return financials.lessDebt;
  }, [debtOverride, financials.lessDebt]);

  // Track whether values come from financials
  const cashIsFromFinancials = useMemo(() => isNil(cashOverride), [cashOverride]);

  const debtIsFromFinancials = useMemo(() => isNil(debtOverride), [debtOverride]);

  // Calculate equity value
  const calculatedEquityValue = useMemo(() => {
    // If enterprise value is set, calculate equity from it
    if (!isNil(enterpriseValue)) {
      return EQUITY_VALUE(
        Number(enterpriseValue),
        Number(effectiveCash),
        Number(effectiveDebt)
      );
    }
    // If equity value is directly set, use it
    if (!isNil(equityValue)) {
      return equityValue;
    }
    // No value available
    return null;
  }, [enterpriseValue, equityValue, effectiveCash, effectiveDebt]);

  const hasValidEquityValue = useMemo(() => calculatedEquityValue !== null && calculatedEquityValue > 0, [calculatedEquityValue]);

  return {
    effectiveCash,
    effectiveDebt,
    calculatedEquityValue,
    cashIsFromFinancials,
    debtIsFromFinancials,
    hasValidEquityValue,
  };
};
